/* ==========================================================================
   FILE: pages/AuthPage.jsx
   IAMONEAI - Authentication Page
   Login and registration page
   ========================================================================== */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthLayout } from '../global/layouts';
import { LoginForm, RegisterForm } from '../modules/auth';
import { useApp } from '../global/context/AppContext';
import './AuthPage.css';

// ==========================================================================
// AUTH PAGE COMPONENT
// ==========================================================================

function AuthPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, login, isLoading } = useApp();

  // Determine initial mode from URL
  const initialMode = searchParams.get('mode') || 'login';
  const [mode, setMode] = useState(initialMode);

  // Redirect if already logged in
  useEffect(() => {
    if (user && !isLoading) {
      navigate('/dashboard');
    }
  }, [user, isLoading, navigate]);

  // Update mode when URL changes
  useEffect(() => {
    const urlMode = searchParams.get('mode');
    if (urlMode && (urlMode === 'login' || urlMode === 'register')) {
      setMode(urlMode);
    }
  }, [searchParams]);

  // ==========================================================================
  // HANDLERS
  // ==========================================================================

  /**
   * Handle successful login from LoginForm
   * LoginForm passes: { iin, brain, identity }
   */
  const handleLoginSuccess = async (userData) => {
    try {
      const { iin, brain, identity } = userData;
      
      // Call context login with the data already fetched by LoginForm
      login(iin, identity, brain);
      
      // Navigate to dashboard
      navigate('/dashboard');
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const handleRegisterComplete = (userData) => {
    // After registration, redirect to login with the new IIN
    setMode('login');
  };

  const handleSwitchMode = (newMode) => {
    setMode(newMode);
    navigate(`/auth?mode=${newMode}`, { replace: true });
  };

  const handleForgotIIN = () => {
    alert('Forgot IIN feature coming soon. Please check your email for your IIN.');
  };

  // ==========================================================================
  // RENDER
  // ==========================================================================

  // Don't render if checking auth state
  if (isLoading) {
    return (
      <div className="auth-page-loading">
        <div className="auth-loading-spinner" />
        <span>Loading...</span>
      </div>
    );
  }

  // Don't render if already logged in (will redirect)
  if (user) {
    return null;
  }

  return (
    <AuthLayout
      title={mode === 'login' ? 'Welcome Back' : 'Create Your Identity'}
      subtitle={
        mode === 'login'
          ? 'Sign in with your IIN to continue'
          : 'Join IAMONEAI and take control of your AI experience'
      }
    >
      <div className="auth-page">
        {mode === 'login' ? (
          <LoginForm
            onSuccess={handleLoginSuccess}
            onRegisterClick={() => handleSwitchMode('register')}
            onForgotClick={handleForgotIIN}
          />
        ) : (
          <RegisterForm
            onComplete={handleRegisterComplete}
            onLogin={() => handleSwitchMode('login')}
          />
        )}

        {/* Mode Switcher */}
        <div className="auth-mode-switcher">
          {mode === 'login' ? (
            <p>
              Don't have an account?{' '}
              <button onClick={() => handleSwitchMode('register')}>
                Create one
              </button>
            </p>
          ) : (
            <p>
              Already have an account?{' '}
              <button onClick={() => handleSwitchMode('login')}>
                Sign in
              </button>
            </p>
          )}
        </div>
      </div>
    </AuthLayout>
  );
}

// ==========================================================================
// EXPORTS
// ==========================================================================

export default AuthPage;